import { useNavigate } from 'react-router-dom'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { RecentOrdersDropdown } from './RecentOrdersDropdown'
import {
  ArrowLeft,
  Clock,
  Pause,
  BarChart3,
  User,
  Settings
} from 'lucide-react'

interface MobilePOSHeaderProps {
  hasActiveCart: boolean
  suspendedSalesCount: number
  onSuspendSale: () => void
  onShowSuspendedSales: () => void
  recentOrders: any[]
  onReprintReceipt: (orderId: string) => void
  onViewOrder: (orderId: string) => void
  formatCurrency: (amount: number) => string
  onSettings?: () => void
  onReports?: () => void
  onUserManagement?: () => void
}

export const MobilePOSHeader = ({
  hasActiveCart,
  suspendedSalesCount,
  onSuspendSale,
  onShowSuspendedSales,
  recentOrders,
  onReprintReceipt,
  onViewOrder,
  formatCurrency,
  onSettings,
  onReports,
  onUserManagement
}: MobilePOSHeaderProps) => {
  const navigate = useNavigate()
  
  const handleBackToDashboard = () => {
    navigate('/')
  }

  return (
    <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-2 py-2">
      <div className="flex flex-col gap-2">
        {/* Row 1 - Back button and Primary Actions */}
        <div className="flex items-center justify-between">
          {/* Left - Back button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleBackToDashboard}
            className="h-7 w-7 p-0 flex-shrink-0"
          >
            <ArrowLeft className="h-3 w-3" />
          </Button>
          
          {/* Center - Suspend/Hold and Recent Orders */}
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={onShowSuspendedSales}
              className="h-7 px-2 flex items-center gap-1 relative flex-shrink-0 text-xs"
            >
              <Clock className="h-3 w-3" />
              <span>Suspend</span>
              {suspendedSalesCount > 0 && (
                <Badge 
                  variant="destructive" 
                  className="absolute -top-1 -right-1 h-4 w-4 p-0 text-xs flex items-center justify-center"
                >
                  {suspendedSalesCount}
                </Badge>
              )}
            </Button>
            
            {hasActiveCart && (
              <Button
                variant="outline"
                size="sm"
                onClick={onSuspendSale}
                className="h-7 px-2 flex items-center gap-1 flex-shrink-0 text-xs text-orange-600 hover:text-orange-700"
              >
                <Pause className="h-3 w-3" />
                <span>Hold</span>
              </Button>
            )}
            
            <RecentOrdersDropdown
              recentOrders={recentOrders}
              onReprintReceipt={onReprintReceipt}
              onViewOrder={onViewOrder}
              formatCurrency={formatCurrency}
              compact={true}
            />
          </div>
          
          {/* Placeholder for balance */}
          <div className="w-7 h-7"></div>
        </div>
        
        {/* Row 2 - Secondary Actions */}
        <div className="flex items-center justify-end gap-1">
          {onReports && (
            <Button
              variant="outline"
              size="sm"
              onClick={onReports}
              className="h-7 w-7 p-0 flex items-center justify-center flex-shrink-0"
            >
              <BarChart3 className="h-3 w-3" />
            </Button>
          )}

          {onSettings && (
            <Button
              variant="outline"
              size="sm"
              onClick={onSettings}
              className="h-7 w-7 p-0 flex items-center justify-center flex-shrink-0"
            >
              <Settings className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}